import React, { useState, useRef, useEffect } from 'react';
import {
  Share2, Loader2, Download, Trash2, Copy, Check,
  ChevronLeft, ChevronRight, Archive, RotateCcw, ImagePlus, Save, Sparkles, X, Trees, Quote,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { useSermons } from '../../hooks/useSermons';
import { useSocialMediaPosts, SocialMediaPost } from '../../hooks/useSocialMediaPosts';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';

// ── Types ─────────────────────────────────────────────────────────────────────

type Style = 'elegant' | 'bold' | 'minimalist';
type Platform = 'instagram' | 'instagram45' | 'story' | 'facebook';
type PostType = 'single' | 'carousel';
type BgPromptType = 'nature' | 'quote';

interface PlatformConfig {
  id: Platform;
  label: string;
  size: string;
  width: number;
  height: number;
  geminiAspectRatio: string;
}

interface BoldColor {
  id: string;
  label: string;
  value: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const PLATFORMS: PlatformConfig[] = [
  { id: 'instagram',   label: 'Instagram Square',   size: '1:1',    width: 1080, height: 1080, geminiAspectRatio: '1:1'  },
  { id: 'instagram45', label: 'Instagram Portrait',  size: '4:5',    width: 1080, height: 1350, geminiAspectRatio: '3:4'  },
  { id: 'story',       label: 'Story / Reel',        size: '9:16',   width: 1080, height: 1920, geminiAspectRatio: '9:16' },
  { id: 'facebook',    label: 'Facebook / LinkedIn', size: '1.91:1', width: 1200, height: 630,  geminiAspectRatio: '16:9' },
];

const BOLD_COLORS: BoldColor[] = [
  { id: 'electric', label: 'Electric Blue', value: '#2874A6' },
  { id: 'crimson',  label: 'Crimson',       value: '#B22A2F' },
  { id: 'fiery',    label: 'Fiery Orange',  value: '#E86A33' },
  { id: 'emerald',  label: 'Emerald',       value: '#2D7F5E' },
  { id: 'gold',     label: 'Gold',          value: '#C49A2A' },
  { id: 'midnight', label: 'Midnight',      value: '#1C2B39' },
];

const STYLE_META = {
  elegant: {
    label: 'Elegant',
    descriptor: 'Serif · Gold · Dark',
    swatchBg: 'linear-gradient(160deg, #1C2B39 0%, #0d1721 100%)',
    swatchAccent: '#D9A441',
  },
  bold: {
    label: 'Bold',
    descriptor: 'Vibrant · Impact · Color',
    swatchBg: '#2874A6',
    swatchAccent: 'white',
  },
  minimalist: {
    label: 'Minimalist',
    descriptor: 'Clean · Light · Airy',
    swatchBg: '#F9F7F4',
    swatchAccent: '#4F5D75',
  },
};

const PLATFORM_LABELS: Record<string, string> = {
  instagram: 'Instagram', instagram45: 'IG Portrait', story: 'Story/Reel', facebook: 'Facebook',
};

const SLIDE_COUNTS = [4, 5, 6, 7];
const PREVIEW_W = 280;

// ── Helpers ───────────────────────────────────────────────────────────────────

function computeSlideFont(captionLen: number, style: Style): number {
  const bases: Record<Style, number> = { elegant: 72, bold: 90, minimalist: 64 };
  const base = bases[style];
  if (captionLen > 200) return Math.floor(base * 0.62);
  if (captionLen > 140) return Math.floor(base * 0.76);
  if (captionLen > 90)  return Math.floor(base * 0.88);
  return base;
}

function buildBgPrompt(type: BgPromptType, caption: string, style: Style, sermonTitle: string, scripture: string): string {
  if (type === 'nature') {
    const byStyle: Record<Style, string> = {
      elegant: 'Cinematic nature photograph. Dramatic moody sky over ancient mountain peaks or cathedral cliffs at dusk. Deep navy blues and gold light rays breaking through storm clouds. Majestic, spiritual, contemplative. No people, no text, no symbols.',
      bold: 'Vibrant dramatic nature scene. Fiery red-orange sunset over rugged mountains, crashing ocean waves, or volcanic landscape. Bold saturated colors, high contrast, powerful. No people, no text.',
      minimalist: 'Serene minimal nature photograph. Misty morning forest path, soft bokeh meadow, or still lake reflection at dawn. Soft diffused light, muted pale palette, deeply peaceful. No people, no text.',
    };
    return byStyle[style];
  }
  // Quote-based
  const styleAtmosphere: Record<Style, string> = {
    elegant: 'Dark moody cinematic atmosphere. Deep rich colors, dramatic contrast, spiritual gravitas.',
    bold: 'Bold vibrant visual energy. Powerful colors, dynamic composition, strong impact.',
    minimalist: 'Soft minimal serene aesthetic. Gentle light, subtle texture, peaceful simplicity.',
  };
  const themeHint = [sermonTitle, scripture].filter(Boolean).join(' — ');
  return `Create an evocative abstract visual background inspired by this quote: "${caption.slice(0, 180)}". ${themeHint ? `Theme context: ${themeHint}. ` : ''}${styleAtmosphere[style]} No text, no faces, no readable words. High-quality church media background.`;
}

// ── Canvas Template ───────────────────────────────────────────────────────────

interface CanvasTemplateProps {
  style: Style;
  width: number;
  height: number;
  caption: string;
  attribution?: string;
  sermonTitle?: string;
  boldColor: string;
  bgImageUrl?: string | null;
}

function CanvasTemplate({ style, width, height, caption, attribution, sermonTitle, boldColor, bgImageUrl }: CanvasTemplateProps) {
  const fontSize = computeSlideFont(caption.length, style);
  const hpad = Math.round(width * 0.082);
  const vFooter = Math.round(height * 0.075);

  if (style === 'elegant') {
    return (
      <div style={{ position: 'relative', width, height, overflow: 'hidden', background: '#0d1721' }}>
        {bgImageUrl && (
          <img src={bgImageUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
        )}
        <div style={{ position: 'absolute', inset: 0, background: bgImageUrl ? 'linear-gradient(160deg, rgba(28,43,57,0.86) 0%, rgba(13,23,33,0.92) 100%)' : 'linear-gradient(160deg, #1C2B39 0%, #0d1721 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 20% 80%, rgba(255,255,255,0.05), transparent 60%)' }} />
        <div style={{ position: 'absolute', top: Math.round(height * 0.09), left: hpad }}>
          <div style={{ width: 4, height: 72, background: '#D9A441', borderRadius: 2 }} />
          <span style={{ position: 'absolute', top: -24, left: 18, fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 260, lineHeight: 1, color: '#D9A441', opacity: 0.11, fontStyle: 'italic', userSelect: 'none' }}>"</span>
        </div>
        <div style={{ position: 'absolute', inset: 0, padding: `${Math.round(height * 0.21)}px ${hpad}px`, display: 'flex', alignItems: 'center' }}>
          <p style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontWeight: '400', fontStyle: 'italic', fontSize: `${fontSize}px`, lineHeight: '1.28', color: '#F2E8D5', wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0 }}>
            {caption}
          </p>
        </div>
        <div style={{ position: 'absolute', bottom: vFooter, left: hpad, right: hpad }}>
          <div style={{ height: 1, background: '#D9A441', opacity: 0.3, marginBottom: 16 }} />
          {attribution && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 30, fontWeight: 600, color: 'rgba(242,232,213,0.85)', margin: '0 0 8px 0', letterSpacing: '0.01em' }}>{attribution}</p>}
          {sermonTitle && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 26, fontWeight: 400, color: 'rgba(242,232,213,0.45)', margin: '0 0 8px 0' }}>{sermonTitle}</p>}
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 20, fontWeight: 400, color: 'rgba(242,232,213,0.22)', letterSpacing: '0.14em', margin: 0 }}>sermonbuild.com</p>
        </div>
      </div>
    );
  }

  if (style === 'bold') {
    return (
      <div style={{ position: 'relative', width, height, overflow: 'hidden', background: boldColor }}>
        {bgImageUrl && (
          <img src={bgImageUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
        )}
        <div style={{ position: 'absolute', inset: 0, background: bgImageUrl ? `${boldColor}cc` : boldColor }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 10, background: 'rgba(0,0,0,0.22)' }} />
        <div style={{ position: 'absolute', left: hpad, right: hpad, top: Math.round(height * 0.32), bottom: Math.round(height * 0.2), display: 'flex', alignItems: 'flex-start' }}>
          <p style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontWeight: '400', fontSize: `${fontSize}px`, lineHeight: '1.12', color: 'white', wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0 }}>
            {caption}
          </p>
        </div>
        <div style={{ position: 'absolute', bottom: vFooter, left: hpad, right: hpad }}>
          {attribution && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 30, fontWeight: 700, color: 'rgba(255,255,255,0.9)', margin: '0 0 6px 0' }}>{attribution}</p>}
          {sermonTitle && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 26, fontWeight: 500, color: 'rgba(255,255,255,0.6)', margin: '0 0 6px 0' }}>{sermonTitle}</p>}
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 20, fontWeight: 400, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.14em', margin: 0 }}>sermonbuild.com</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, height, overflow: 'hidden', background: '#F9F7F4' }}>
      {bgImageUrl && (
        <img src={bgImageUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
      )}
      {bgImageUrl && <div style={{ position: 'absolute', inset: 0, background: 'rgba(249,247,244,0.82)' }} />}
      <div style={{ position: 'absolute', top: Math.round(height * 0.11), left: hpad, right: hpad, height: 1, background: '#4F5D75', opacity: 0.18 }} />
      <div style={{ position: 'absolute', inset: 0, padding: `${Math.round(height * 0.19)}px ${Math.round(width * 0.1)}px`, display: 'flex', alignItems: 'center' }}>
        <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: '300', fontSize: `${fontSize}px`, lineHeight: '1.5', color: '#1C2B39', wordBreak: 'break-word', overflowWrap: 'break-word', margin: 0 }}>
          "{caption}"
        </p>
      </div>
      <div style={{ position: 'absolute', bottom: vFooter, left: hpad, right: hpad }}>
        <div style={{ height: 1, background: '#4F5D75', opacity: 0.15, marginBottom: 14 }} />
        {attribution && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 28, fontWeight: 500, color: '#1C2B39', margin: '0 0 6px 0' }}>{attribution}</p>}
        {sermonTitle && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 400, color: '#4F5D75', margin: '0 0 6px 0' }}>{sermonTitle}</p>}
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 19, fontWeight: 300, color: '#4F5D75', opacity: 0.5, letterSpacing: '0.12em', margin: 0 }}>sermonbuild.com</p>
      </div>
    </div>
  );
}

// ── Style Picker Card ─────────────────────────────────────────────────────────

function StyleCard({ id, selected, boldColor, onClick }: { id: Style; selected: boolean; boldColor: string; onClick: () => void }) {
  const meta = STYLE_META[id];
  const swatchBg = id === 'bold' ? boldColor : meta.swatchBg;

  return (
    <button
      onClick={onClick}
      className={`relative flex flex-col overflow-hidden rounded-xl border-2 transition-all text-left ${
        selected
          ? 'border-electric shadow-md shadow-electric/20 scale-[1.02]'
          : 'border-sand/30 dark:border-slate/30 hover:border-electric/40 hover:shadow-sm'
      }`}
    >
      {/* Visual swatch */}
      <div className="h-12 w-full relative overflow-hidden" style={{ background: swatchBg }}>
        {id === 'elegant' && (
          <>
            <div className="absolute left-3 top-2 w-0.5 h-5 rounded-full" style={{ background: '#D9A441' }} />
            <span className="absolute left-4 top-0 text-5xl leading-none font-serif italic" style={{ color: '#D9A441', opacity: 0.18 }}>"</span>
          </>
        )}
        {id === 'bold' && (
          <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'rgba(0,0,0,0.22)' }} />
        )}
        {id === 'minimalist' && (
          <div className="absolute top-3 left-3 right-3 h-px" style={{ background: '#4F5D75', opacity: 0.25 }} />
        )}
      </div>
      {/* Label */}
      <div className={`px-3 py-2 ${selected ? 'bg-electric/5 dark:bg-electric/10' : 'bg-white dark:bg-charcoal'}`}>
        <p className={`text-sm font-semibold leading-tight ${selected ? 'text-electric' : 'text-midnight dark:text-soft-white'}`}>
          {meta.label}
        </p>
        <p className="text-[10px] text-slate/50 dark:text-soft-white/30 mt-0.5">{meta.descriptor}</p>
      </div>
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-electric flex items-center justify-center">
          <Check className="h-2.5 w-2.5 text-white" />
        </div>
      )}
    </button>
  );
}

// ── Platform Pill ─────────────────────────────────────────────────────────────

function PlatformPill({ platform, selected, onClick }: { platform: PlatformConfig; selected: boolean; onClick: () => void }) {
  const { width, height, label, size } = platform;
  const ratio = width / height;
  const pillW = ratio >= 1 ? 18 : Math.round(18 * ratio);
  const pillH = ratio >= 1 ? Math.round(18 / ratio) : 18;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium ${
        selected
          ? 'bg-electric text-white border-electric shadow-sm shadow-electric/30'
          : 'bg-white dark:bg-charcoal text-slate dark:text-soft-white/60 border-sand/30 dark:border-slate/30 hover:border-electric/50 hover:text-electric dark:hover:text-electric'
      }`}
    >
      <span
        className={`inline-block rounded-sm flex-shrink-0 border ${selected ? 'border-white/60' : 'border-current opacity-50'}`}
        style={{ width: pillW, height: pillH }}
      />
      <span>{label}</span>
      <span className={`text-xs ${selected ? 'text-white/70' : 'text-slate/40 dark:text-soft-white/30'}`}>{size}</span>
    </button>
  );
}

// ── Saved Post Card ───────────────────────────────────────────────────────────

interface SavedPostCardProps {
  post: SocialMediaPost;
  getPublicUrl: (path: string) => string;
  onDelete: (post: SocialMediaPost) => Promise<void>;
  onToggleArchive: (id: string, current: boolean) => Promise<void>;
}

function SavedPostCard({ post, getPublicUrl, onDelete, onToggleArchive }: SavedPostCardProps) {
  const [deleting, setDeleting] = useState(false);
  const firstImage = post.image_paths?.[0];
  const imgUrl = firstImage ? getPublicUrl(firstImage) : null;

  const downloadSingle = async (path: string) => {
    const url = getPublicUrl(path);
    const res = await fetch(url);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl; a.download = `post-${path.split('/').pop() || 'image'}`; a.click();
    URL.revokeObjectURL(objUrl);
  };

  const downloadZip = async () => {
    const zip = new JSZip();
    for (let i = 0; i < post.image_paths.length; i++) {
      const url = getPublicUrl(post.image_paths[i]);
      const res = await fetch(url);
      zip.file(`slide-${String(i + 1).padStart(2, '0')}.png`, await res.blob());
    }
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const objUrl = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = objUrl; a.download = `carousel-${Date.now()}.zip`; a.click();
    URL.revokeObjectURL(objUrl);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post and its images?')) return;
    setDeleting(true);
    try { await onDelete(post); } finally { setDeleting(false); }
  };

  return (
    <div className="group rounded-xl border border-sand/20 dark:border-slate/20 overflow-hidden bg-white dark:bg-charcoal hover:shadow-md transition-shadow">
      <div className="aspect-square bg-sand/20 dark:bg-slate/20 overflow-hidden relative">
        {imgUrl ? (
          <img src={imgUrl} alt="Social post" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Share2 className="h-6 w-6 text-slate/30" />
          </div>
        )}
        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-midnight/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {post.is_carousel ? (
            <button onClick={downloadZip} className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors" title="Download ZIP">
              <Download className="h-4 w-4" />
            </button>
          ) : firstImage ? (
            <button onClick={() => downloadSingle(firstImage)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors" title="Download PNG">
              <Download className="h-4 w-4" />
            </button>
          ) : null}
          <button onClick={() => onToggleArchive(post.id, post.archived)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
            {post.archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </button>
          <button onClick={handleDelete} disabled={deleting} className="p-2 bg-crimson/70 hover:bg-crimson rounded-full text-white transition-colors">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="p-2.5">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="px-1.5 py-0.5 bg-electric/10 text-electric text-[10px] font-semibold rounded-full">
            {PLATFORM_LABELS[post.platform] || post.platform}
          </span>
          <span className="px-1.5 py-0.5 bg-gold/10 text-gold text-[10px] font-semibold rounded-full capitalize">
            {post.style}
          </span>
          {post.is_carousel && (
            <span className="px-1.5 py-0.5 bg-emerald/10 text-emerald text-[10px] font-semibold rounded-full">
              {post.image_paths?.length ?? 0} slides
            </span>
          )}
        </div>
        <p className="text-[10px] text-slate/40 dark:text-soft-white/25 mt-1">
          {new Date(post.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function SocialMediaAssets({ defaultSermonId }: { defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const { posts, loading: postsLoading, savePost, deletePost, toggleArchive, getPublicUrl } = useSocialMediaPosts();

  // Setup
  const [selectedSermonId, setSelectedSermonId] = useState(defaultSermonId ?? '');
  const [attribution, setAttribution] = useState('');
  const [style, setStyle] = useState<Style>('elegant');
  const [platformId, setPlatformId] = useState<Platform>('instagram');
  const [postType, setPostType] = useState<PostType>('single');
  const [slideCount, setSlideCount] = useState(5);
  const [boldColor, setBoldColor] = useState(BOLD_COLORS[0].value);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Compose
  const [captions, setCaptions] = useState<string[]>([]);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [selectedCaptionIndex, setSelectedCaptionIndex] = useState(0);
  const [editedCaptions, setEditedCaptions] = useState<Record<number, string>>({});
  const [previewSlide, setPreviewSlide] = useState(0);
  const [hashtagsCopied, setHashtagsCopied] = useState(false);

  // AI background
  const [bgPromptType, setBgPromptType] = useState<BgPromptType>('nature');
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);
  const [generatingBg, setGeneratingBg] = useState(false);
  const [bgError, setBgError] = useState<string | null>(null);

  // Capture / save
  const [displayCaption, setDisplayCaption] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const captureRef = useRef<HTMLDivElement>(null);

  const platformConfig = PLATFORMS.find(p => p.id === platformId)!;
  const previewScale = PREVIEW_W / platformConfig.width;
  const previewH = Math.round(PREVIEW_W * (platformConfig.height / platformConfig.width));
  const sermon = sermons.find(s => s.id === selectedSermonId);
  const isBusy = capturing || saving;
  const hasCompose = captions.length > 0;

  const activeCaption = postType === 'single'
    ? (editedCaptions[selectedCaptionIndex] ?? captions[selectedCaptionIndex] ?? '')
    : (editedCaptions[previewSlide] ?? captions[previewSlide] ?? '');

  useEffect(() => {
    if (captions.length === 0) return;
    setDisplayCaption(activeCaption);
  }, [postType, selectedCaptionIndex, previewSlide, captions, editedCaptions]);

  const generateCaptions = async () => {
    if (!selectedSermonId) return;
    try {
      setGenerating(true);
      setGenerationError(null);
      const s = sermons.find(s => s.id === selectedSermonId);
      if (!s) throw new Error('Sermon not found');

      const session = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          type: 'enhance',
          content: s.content,
          enhancementType: 'social_media_quotes',
          additionalData: { slideCount: postType === 'carousel' ? slideCount : 4, isCarousel: postType === 'carousel' },
        }),
      });

      if (!response.ok) throw new Error('Failed to generate captions');
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to generate captions');

      const raw = typeof result.content === 'string' ? result.content : JSON.stringify(result.content);
      const cleaned = raw.replace(/```(?:json)?/g, '').trim();
      const parsed = JSON.parse(cleaned);
      if (!Array.isArray(parsed.captions) || !Array.isArray(parsed.hashtags)) {
        throw new Error('Unexpected AI response format. Please try again.');
      }

      setCaptions(parsed.captions);
      setHashtags(parsed.hashtags);
      setSelectedCaptionIndex(0);
      setEditedCaptions({});
      setPreviewSlide(0);
      setBgImageUrl(null);
    } catch (err) {
      logger.error('Error generating social captions:', err);
      setGenerationError(err instanceof Error ? err.message : 'Failed to generate captions');
    } finally {
      setGenerating(false);
    }
  };

  const generateBackground = async () => {
    try {
      setGeneratingBg(true);
      setBgError(null);
      const s = sermons.find(s => s.id === selectedSermonId);
      const prompt = buildBgPrompt(bgPromptType, activeCaption, style, s?.title ?? '', s?.scripture ?? '');
      const imageStyleMap: Record<Style, string> = { elegant: 'dramatic', bold: 'bold', minimalist: 'modern' };

      const session = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-image-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ prompt, imageStyle: imageStyleMap[style], aspectRatio: platformConfig.geminiAspectRatio, imageSize: '1K' }),
      });

      if (!response.ok) throw new Error('Failed to generate background');
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Failed to generate background');
      const url = result.content?.[0]?.url;
      if (!url) throw new Error('No image returned');
      setBgImageUrl(url);
    } catch (err) {
      logger.error('Background generation error:', err);
      setBgError(err instanceof Error ? err.message : 'Failed to generate background');
    } finally {
      setGeneratingBg(false);
    }
  };

  const captureToBlob = async (): Promise<Blob> => {
    if (!captureRef.current) throw new Error('Capture target not ready');
    await document.fonts.ready;
    await new Promise(r => setTimeout(r, 200));
    const canvas = await html2canvas(captureRef.current, {
      width: platformConfig.width, height: platformConfig.height,
      scale: 1, useCORS: true, allowTaint: false, logging: false,
      onclone: (clonedDoc) => {
        const styleEl = clonedDoc.createElement('style');
        styleEl.textContent = `@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@300;400;500;600;700&display=swap');`;
        clonedDoc.head.appendChild(styleEl);
      },
    });
    return new Promise((resolve, reject) => {
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png');
    });
  };

  const getActiveCaptions = (): string[] => {
    if (postType === 'single') return [editedCaptions[selectedCaptionIndex] ?? captions[selectedCaptionIndex] ?? ''];
    return captions.map((c, i) => editedCaptions[i] ?? c);
  };

  const handleDownloadSingle = async () => {
    try {
      setCapturing(true);
      setDisplayCaption(getActiveCaptions()[0]);
      await new Promise(r => setTimeout(r, 250));
      const blob = await captureToBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `social-post-${platformId}-${Date.now()}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { logger.error('Download error:', err); }
    finally { setCapturing(false); }
  };

  const handleDownloadZip = async () => {
    try {
      setCapturing(true);
      const zip = new JSZip();
      const activeCaptions = getActiveCaptions();
      for (let i = 0; i < activeCaptions.length; i++) {
        setDisplayCaption(activeCaptions[i]);
        await new Promise(r => setTimeout(r, 300));
        zip.file(`slide-${String(i + 1).padStart(2, '0')}.png`, await captureToBlob());
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url; a.download = `social-carousel-${Date.now()}.zip`; a.click();
      URL.revokeObjectURL(url);
      setDisplayCaption(editedCaptions[previewSlide] ?? captions[previewSlide] ?? '');
    } catch (err) { logger.error('ZIP download error:', err); }
    finally { setCapturing(false); }
  };

  const handleSave = async () => {
    try {
      setSaving(true); setSaveError(null);
      const activeCaptions = getActiveCaptions();
      const imageBlobs: Blob[] = [];
      for (let i = 0; i < activeCaptions.length; i++) {
        setDisplayCaption(activeCaptions[i]);
        await new Promise(r => setTimeout(r, 300));
        imageBlobs.push(await captureToBlob());
      }
      await savePost({ sermon_id: selectedSermonId || undefined, style, platform: platformId, is_carousel: postType === 'carousel', captions: activeCaptions, hashtags, imageBlobs });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setDisplayCaption(editedCaptions[previewSlide] ?? captions[previewSlide] ?? '');
    } catch (err) {
      logger.error('Save error:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const copyHashtags = () => {
    navigator.clipboard.writeText(hashtags.join(' ')).then(() => {
      setHashtagsCopied(true);
      setTimeout(() => setHashtagsCopied(false), 2000);
    });
  };

  const sharedTemplateProps = {
    style, width: platformConfig.width, height: platformConfig.height,
    caption: displayCaption, attribution: attribution || undefined,
    sermonTitle: sermon?.title, boldColor, bgImageUrl,
  };

  return (
    <div className="space-y-5">
      {/* Off-screen capture target */}
      <div ref={captureRef} aria-hidden="true" style={{ position: 'fixed', left: '-99999px', top: 0, width: platformConfig.width, height: platformConfig.height, overflow: 'hidden' }}>
        <CanvasTemplate {...sharedTemplateProps} />
      </div>

      {/* ── STEP 1: SETUP ───────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-sand/20 dark:border-slate/20 overflow-hidden bg-white dark:bg-charcoal">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-sand/20 dark:border-slate/20 bg-sand/20 dark:bg-slate/10">
          <span className="w-6 h-6 rounded-full bg-electric text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
          <div>
            <h2 className="text-sm font-bold text-midnight dark:text-soft-white">Setup</h2>
            <p className="text-xs text-slate/50 dark:text-soft-white/40">Sermon, attribution, style, and platform</p>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Sermon + Attribution */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1.5">
                Sermon
              </label>
              <select
                value={selectedSermonId}
                onChange={e => setSelectedSermonId(e.target.value)}
                className="w-full rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric/30"
              >
                <option value="">Select a sermon…</option>
                {sermons.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1.5">
                Attribution
              </label>
              <input
                type="text"
                value={attribution}
                onChange={e => setAttribution(e.target.value)}
                placeholder="Pastor John Doe"
                className="w-full rounded-lg border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-midnight dark:text-soft-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-electric/30 placeholder:text-slate/30"
              />
            </div>
          </div>

          {/* Style picker — visual cards */}
          <div>
            <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">
              Design Style
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['elegant', 'bold', 'minimalist'] as Style[]).map(s => (
                <StyleCard
                  key={s} id={s} selected={style === s} boldColor={boldColor}
                  onClick={() => { setStyle(s); setBgImageUrl(null); }}
                />
              ))}
            </div>
            {style === 'bold' && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-sand/20 dark:border-slate/20">
                <span className="text-xs font-medium text-slate/60">Color:</span>
                {BOLD_COLORS.map(c => (
                  <button
                    key={c.id} onClick={() => setBoldColor(c.value)} title={c.label}
                    className={`w-7 h-7 rounded-full transition-all border-2 ${boldColor === c.value ? 'border-electric scale-110 shadow-sm' : 'border-transparent hover:scale-105'}`}
                    style={{ background: c.value }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Platform + Post type row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">
                Platform Size
              </label>
              <div className="flex flex-col gap-1.5">
                {PLATFORMS.map(p => (
                  <PlatformPill key={p.id} platform={p} selected={platformId === p.id} onClick={() => { setPlatformId(p.id); setBgImageUrl(null); }} />
                ))}
              </div>
            </div>

            <div>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">
                  Post Type
                </label>
                <div className="flex rounded-lg overflow-hidden border border-sand/30 dark:border-slate/30">
                  {(['single', 'carousel'] as PostType[]).map(t => (
                    <button
                      key={t}
                      onClick={() => setPostType(t)}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${
                        postType === t
                          ? 'bg-electric text-white'
                          : 'text-slate dark:text-soft-white/60 hover:bg-sand/30 dark:hover:bg-slate/20'
                      }`}
                    >
                      {t === 'single' ? 'Single' : 'Carousel'}
                    </button>
                  ))}
                </div>
              </div>

              {postType === 'carousel' && (
                <div>
                  <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-2">
                    Slides
                  </label>
                  <div className="flex gap-1.5">
                    {SLIDE_COUNTS.map(n => (
                      <button
                        key={n} onClick={() => setSlideCount(n)}
                        className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all ${
                          slideCount === n ? 'bg-electric text-white border-electric' : 'bg-white dark:bg-charcoal text-slate dark:text-soft-white/60 border-sand/30 dark:border-slate/30 hover:border-electric/50'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {generationError && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-crimson/10 text-crimson text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-crimson flex-shrink-0" />
              {generationError}
            </div>
          )}

          <button
            onClick={generateCaptions}
            disabled={!selectedSermonId || generating}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-electric text-white rounded-xl font-semibold hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm shadow-electric/20 text-sm"
          >
            {generating
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating Captions…</>
              : <><ImagePlus className="h-4 w-4" /> Generate Captions</>}
          </button>
        </div>
      </div>

      {/* ── STEP 2: COMPOSE ─────────────────────────────────────────────── */}
      {hasCompose && (
        <div className="rounded-2xl border border-sand/20 dark:border-slate/20 overflow-hidden bg-white dark:bg-charcoal">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-sand/20 dark:border-slate/20 bg-sand/20 dark:bg-slate/10">
            <span className="w-6 h-6 rounded-full bg-electric text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <div>
              <h2 className="text-sm font-bold text-midnight dark:text-soft-white">Compose</h2>
              <p className="text-xs text-slate/50 dark:text-soft-white/40">
                {postType === 'single' ? 'Pick a caption variant' : 'Edit per-slide captions'}
              </p>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">

              {/* Left: Captions + hashtags */}
              <div className="space-y-4 min-w-0">
                {postType === 'single' ? (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider">Caption Variants</p>
                    {captions.map((caption, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedCaptionIndex(i)}
                        className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all text-sm leading-relaxed group ${
                          selectedCaptionIndex === i
                            ? 'border-electric bg-electric/5 dark:bg-electric/10'
                            : 'border-sand/30 dark:border-slate/30 hover:border-electric/40 bg-white dark:bg-charcoal'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-[10px] font-bold transition-colors ${
                            selectedCaptionIndex === i ? 'border-electric bg-electric text-white' : 'border-sand/50 text-slate/40'
                          }`}>
                            {selectedCaptionIndex === i ? <Check className="h-3 w-3" /> : i + 1}
                          </span>
                          <span className={`flex-1 ${selectedCaptionIndex === i ? 'text-midnight dark:text-soft-white' : 'text-slate dark:text-soft-white/60'}`}>
                            {editedCaptions[i] ?? caption}
                          </span>
                        </div>
                      </button>
                    ))}
                    <div className="pt-1">
                      <label className="block text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1.5">
                        Edit Quote
                      </label>
                      <textarea
                        value={editedCaptions[selectedCaptionIndex] ?? captions[selectedCaptionIndex] ?? ''}
                        onChange={e => setEditedCaptions(prev => ({ ...prev, [selectedCaptionIndex]: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2.5 rounded-xl border border-sand/30 dark:border-slate/30 bg-white dark:bg-charcoal text-sm text-midnight dark:text-soft-white resize-none focus:outline-none focus:ring-2 focus:ring-electric/30 leading-relaxed"
                        placeholder="Edit the caption text…"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider">Slide Captions</p>
                    {captions.map((caption, i) => (
                      <div
                        key={i}
                        className={`rounded-xl border-2 transition-all overflow-hidden ${
                          previewSlide === i ? 'border-electric' : 'border-sand/30 dark:border-slate/30'
                        }`}
                      >
                        <div className={`flex items-center justify-between px-3 py-2 ${previewSlide === i ? 'bg-electric/5 dark:bg-electric/10' : 'bg-sand/10 dark:bg-slate/10'}`}>
                          <span className="text-xs font-bold text-electric">Slide {i + 1}</span>
                          <button onClick={() => setPreviewSlide(i)} className="text-xs text-slate/50 hover:text-electric transition-colors font-medium">
                            Preview →
                          </button>
                        </div>
                        <textarea
                          value={editedCaptions[i] ?? caption}
                          onChange={e => setEditedCaptions(prev => ({ ...prev, [i]: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 bg-transparent text-sm text-midnight dark:text-soft-white resize-none focus:outline-none leading-relaxed"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {hashtags.length > 0 && (
                  <div className="rounded-xl border border-sand/20 dark:border-slate/20 p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider">Hashtags</p>
                      <button onClick={copyHashtags} className="flex items-center gap-1.5 text-xs font-medium text-slate/60 hover:text-electric transition-colors px-2 py-1 rounded-md hover:bg-electric/5">
                        {hashtagsCopied ? <><Check className="h-3 w-3 text-emerald" /><span className="text-emerald">Copied!</span></> : <><Copy className="h-3 w-3" /> Copy all</>}
                      </button>
                    </div>
                    <p className="text-xs text-electric/70 leading-relaxed">{hashtags.join(' ')}</p>
                  </div>
                )}
              </div>

              {/* Right: Preview + background + actions */}
              <div className="flex flex-col items-center gap-4">
                {/* Preview */}
                <div className="w-full flex flex-col items-center">
                  <div style={{ width: PREVIEW_W, height: previewH, overflow: 'hidden', borderRadius: 12, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', flexShrink: 0 }}>
                    <div style={{ width: platformConfig.width, height: platformConfig.height, transform: `scale(${previewScale})`, transformOrigin: 'top left' }}>
                      <CanvasTemplate {...sharedTemplateProps} />
                    </div>
                  </div>
                  <p className="text-xs text-slate/40 mt-2">
                    {platformConfig.width} × {platformConfig.height}
                  </p>
                </div>

                {/* Carousel navigation */}
                {postType === 'carousel' && captions.length > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewSlide(p => Math.max(0, p - 1))}
                      disabled={previewSlide === 0}
                      className="p-1.5 rounded-lg border border-sand/30 dark:border-slate/30 text-slate hover:border-electric/50 hover:text-electric disabled:opacity-30 transition-all"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-medium text-slate/60 dark:text-soft-white/40 min-w-[52px] text-center">
                      {previewSlide + 1} / {captions.length}
                    </span>
                    <button
                      onClick={() => setPreviewSlide(p => Math.min(captions.length - 1, p + 1))}
                      disabled={previewSlide === captions.length - 1}
                      className="p-1.5 rounded-lg border border-sand/30 dark:border-slate/30 text-slate hover:border-electric/50 hover:text-electric disabled:opacity-30 transition-all"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* AI Background section */}
                <div className="w-full rounded-xl border border-sand/20 dark:border-slate/20 overflow-hidden">
                  <div className="px-3 py-2.5 bg-sand/20 dark:bg-slate/10 border-b border-sand/20 dark:border-slate/20 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-gold" />
                    <span className="text-xs font-semibold text-midnight dark:text-soft-white">AI Background</span>
                  </div>
                  <div className="p-3 space-y-3">
                    {/* Prompt type toggle */}
                    <div className="flex rounded-lg overflow-hidden border border-sand/30 dark:border-slate/30">
                      <button
                        onClick={() => setBgPromptType('nature')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors ${
                          bgPromptType === 'nature'
                            ? 'bg-emerald text-white'
                            : 'text-slate/60 dark:text-soft-white/40 hover:bg-sand/20 dark:hover:bg-slate/20'
                        }`}
                      >
                        <Trees className="h-3.5 w-3.5" />
                        Nature Scene
                      </button>
                      <button
                        onClick={() => setBgPromptType('quote')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors ${
                          bgPromptType === 'quote'
                            ? 'bg-electric text-white'
                            : 'text-slate/60 dark:text-soft-white/40 hover:bg-sand/20 dark:hover:bg-slate/20'
                        }`}
                      >
                        <Quote className="h-3.5 w-3.5" />
                        Quote-Based
                      </button>
                    </div>

                    <p className="text-[10px] text-slate/50 dark:text-soft-white/30 leading-relaxed">
                      {bgPromptType === 'nature'
                        ? `Generates a ${style} nature scene as background`
                        : 'Generates an image inspired by the selected caption'}
                    </p>

                    {bgImageUrl ? (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald/5 border border-emerald/20">
                        <img src={bgImageUrl} className="w-10 h-10 rounded-md object-cover flex-shrink-0" alt="AI background" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-semibold text-emerald">Background applied</p>
                          <button onClick={generateBackground} disabled={generatingBg} className="text-[10px] text-slate/50 hover:text-electric transition-colors">
                            {generatingBg ? 'Regenerating…' : 'Regenerate →'}
                          </button>
                        </div>
                        <button onClick={() => setBgImageUrl(null)} className="p-1 text-slate/30 hover:text-crimson transition-colors flex-shrink-0">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={generateBackground}
                        disabled={generatingBg}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r from-electric/90 to-emerald/80 text-white text-xs font-semibold hover:from-electric hover:to-emerald transition-all shadow-sm disabled:opacity-60"
                      >
                        {generatingBg
                          ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                          : <><Sparkles className="h-3.5 w-3.5" /> Generate Background</>}
                      </button>
                    )}
                    {bgError && <p className="text-[10px] text-crimson">{bgError}</p>}
                  </div>
                </div>

                {/* Download / Save */}
                <div className="flex flex-col gap-2 w-full">
                  {postType === 'single' ? (
                    <button
                      onClick={handleDownloadSingle}
                      disabled={isBusy}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-electric text-white rounded-xl font-semibold hover:bg-electric/90 disabled:opacity-60 transition-all text-sm shadow-sm shadow-electric/20"
                    >
                      {capturing ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <><Download className="h-4 w-4" /> Download PNG</>}
                    </button>
                  ) : (
                    <button
                      onClick={handleDownloadZip}
                      disabled={isBusy}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-electric text-white rounded-xl font-semibold hover:bg-electric/90 disabled:opacity-60 transition-all text-sm shadow-sm shadow-electric/20"
                    >
                      {capturing ? <><Loader2 className="h-4 w-4 animate-spin" /> Building ZIP…</> : <><Download className="h-4 w-4" /> Download ZIP</>}
                    </button>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={isBusy}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald text-white rounded-xl font-semibold hover:bg-emerald/90 disabled:opacity-60 transition-all text-sm"
                  >
                    {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                      : saveSuccess ? <><Check className="h-4 w-4" /> Saved!</>
                      : <><Save className="h-4 w-4" /> Save to Sermon</>}
                  </button>
                  {saveError && <p className="text-xs text-crimson text-center">{saveError}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SAVED POSTS ──────────────────────────────────────────────────── */}
      {!postsLoading && posts.length > 0 && (
        <div className="rounded-2xl border border-sand/20 dark:border-slate/20 overflow-hidden bg-white dark:bg-charcoal">
          <div className="flex items-center justify-between px-5 py-4 border-b border-sand/20 dark:border-slate/20 bg-sand/20 dark:bg-slate/10">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-slate/20 dark:bg-slate/40 text-slate dark:text-soft-white/60 text-xs font-bold flex items-center justify-center">3</span>
              <div>
                <h2 className="text-sm font-bold text-midnight dark:text-soft-white">Saved Posts</h2>
                <p className="text-xs text-slate/50 dark:text-soft-white/40">{posts.length} post{posts.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {posts.map(post => (
                <SavedPostCard key={post.id} post={post} getPublicUrl={getPublicUrl} onDelete={deletePost} onToggleArchive={toggleArchive} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
